import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function SharpeRatioCalculator() {
  const { t } = useTranslation();
  const [portfolioReturn, setPortfolioReturn] = useState("");
  const [riskFreeRate, setRiskFreeRate] = useState("");
  const [standardDeviation, setStandardDeviation] = useState("");
  const [result, setResult] = useState<{ sharpe: number; interpretation: string } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: '/' },
      { name: t('categories.finance'), url: '/finance' },
      { name: "Sharpe Ratio Calculator", url: '/calculator/sharpe-ratio' }
    ], lang),
    (lang) => calculatorToolSchema(
      "Sharpe Ratio Calculator",
      "Measure risk-adjusted returns of investments. Enter portfolio return, risk-free rate, and standard deviation to calculate Sharpe Ratio and interpretation.",
      "https://calclab.app/calculator/sharpe-ratio",
      lang
    )
  ]);

  const calculate = () => {
    const portfolioRet = parseFloat(portfolioReturn) / 100;
    const riskFree = parseFloat(riskFreeRate) / 100;
    const stdDev = parseFloat(standardDeviation) / 100;

    if (isNaN(portfolioRet) || isNaN(riskFree) || isNaN(stdDev) || stdDev === 0) {
      toast.error("Please enter valid values (standard deviation cannot be zero)");
      return;
    }

    // Sharpe Ratio = (Portfolio Return - Risk-Free Rate) / Standard Deviation
    const sharpe = (portfolioRet - riskFree) / stdDev;

    let interpretation = "";
    if (sharpe < 0) interpretation = "Poor - Negative excess returns";
    else if (sharpe < 1) interpretation = "Below Average - Weak risk-adjusted returns";
    else if (sharpe < 2) interpretation = "Good - Acceptable risk-adjusted returns";
    else if (sharpe < 3) interpretation = "Very Good - Strong risk-adjusted returns";
    else interpretation = "Excellent - Outstanding risk-adjusted returns";

    setResult({ sharpe, interpretation });
    toast.success("Sharpe Ratio calculated!");
  };

  const reset = () => {
    setPortfolioReturn("");
    setRiskFreeRate("");
    setStandardDeviation("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Sharpe Ratio Calculator</CardTitle>
          <CardDescription>
            Measure risk-adjusted returns of an investment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="return">Portfolio Return (%)</Label>
            <Input
              id="return"
              type="number"
              placeholder="12"
              value={portfolioReturn}
              onChange={(e) => setPortfolioReturn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="riskFree">Risk-Free Rate (%)</Label>
            <Input
              id="riskFree"
              type="number"
              placeholder="3"
              value={riskFreeRate}
              onChange={(e) => setRiskFreeRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stdDev">Standard Deviation (%)</Label>
            <Input
              id="stdDev"
              type="number"
              placeholder="15"
              value={standardDeviation}
              onChange={(e) => setStandardDeviation(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Sharpe Ratio:</strong> {result.sharpe.toFixed(3)}
                </p>
                <CopyResultButton value={result.sharpe.toFixed(3)} />
              </div>
              <p className="text-sm">
                <strong>Interpretation:</strong> {result.interpretation}
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["sharpe-ratio"]} trigger={result?.sharpe} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
